import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '../Shell';
import { useNav } from '../nav';
import { useAsync } from '../../core/useAsync';
import {
  ApiError,
  adminCloseRound, adminCloseSubmissions, adminCreateRound, adminDeleteRound,
  adminOpenRound, adminReorderRound, adminRescore, adminUnopenRound,
  cachedLeague, clearLeagueCache, drawSeason, getCurrentRound, getMe, getMgmtLeague, getProposals, getSeasonRounds, setProposalWindow,
  type LeagueSettings as LS,
} from '../../core/api';
import { gradFor } from '../format';
import type { CurrentRound, LeagueInfo, Me, MediaType, Pace, Phase, Proposal, SeasonRound } from '../../core/types';
import s from './Admin.module.css';

// Admin console: the organiser's control surface, wired to the live mgmt API. Round
// management is now NATIVE here — the whole lifecycle (create → open → close subs →
// close & score, plus un-open / delete / reorder / re-score) without dropping to the
// classic pixel admin. Owner-only (me.is_admin is the caller's role in THIS league).
// People / scoring numbers / Spotify still live in the classic admin (linked below).

const Chev = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m9 6 6 6-6 6" /></svg>;

const PHASE_LABEL: Record<Phase, string> = { SCHEDULED: 'Scheduled', SUBMITTING: 'Submissions open', VOTING: 'Voting', CLOSED: 'Closed' };
const MEDIA: { key: MediaType; label: string }[] = [
  { key: 'audio', label: 'Song (Spotify)' },
  { key: 'image', label: 'Image (upload)' },
  { key: 'video', label: 'Video (YouTube)' },
];
const PACES: { key: Pace; label: string }[] = [
  { key: 'rapid', label: 'Rapid — 24h a phase' },
  { key: 'steady', label: 'Steady — 3 days a phase' },
  { key: 'slow', label: 'Slow — 1 week a phase' },
];

function MRow({ label, sub, onClick }: { label: string; sub: string; onClick?: () => void }) {
  return (
    <button className={s.mrow} onClick={onClick}>
      <span className={s.l}><b>{label}</b><small>{sub}</small></span>
      <span className={s.chev}><Chev /></span>
    </button>
  );
}

export function AdminConsole() {
  const nav = useNav();
  const navigate = useNavigate();
  const me = useAsync<Me>(getMe, []);
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const linfo = useAsync<LeagueInfo>(cachedLeague, []);
  const seasonId = linfo.data?.season_id ?? null;
  const list = useAsync<SeasonRound[]>(() => (seasonId != null ? getSeasonRounds(seasonId) : Promise.resolve([])), [seasonId]);
  const league = useAsync<LS>(getMgmtLeague, []);
  const isJukebox = league.data?.league_type === 'jukebox';
  const proposalsOpen = linfo.data?.proposals_state === 'open';

  const [capStr, setCapStr] = useState('');
  useEffect(() => { if (linfo.data?.proposals_per_member != null) setCapStr(String(linfo.data.proposals_per_member)); }, [linfo.data]);
  async function saveCap() {
    const n = Number(capStr.trim());
    if (busy != null || seasonId == null) return;
    if (!Number.isInteger(n) || n < 1) { setErr('Per-member limit must be a whole number ≥ 1.'); return; }
    setBusy('cap'); setErr(null); setMsg(null);
    try {
      await setProposalWindow(seasonId, { proposals_per_member: n });
      clearLeagueCache(); linfo.reload();
      setMsg(`Members can propose up to ${n} round${n === 1 ? '' : 's'} each.`);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not save the limit.'); }
    finally { setBusy(null); }
  }

  async function setProposals(open: boolean) {
    if (busy != null || seasonId == null) return;
    if (!open && !window.confirm('Close proposals? Members can’t propose until you re-open or draw the season.')) return;
    setBusy('propwin'); setErr(null); setMsg(null);
    try {
      await setProposalWindow(seasonId, { proposals_open: open });
      clearLeagueCache(); linfo.reload();
      setMsg(open ? 'Proposals are open — members can propose rounds now.' : 'Proposals closed.');
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not update proposals.'); }
    finally { setBusy(null); }
  }
  const proposals = useAsync<Proposal[]>(() => (isJukebox ? getProposals() : Promise.resolve([])), [isJukebox]);

  const [busy, setBusy] = useState<number | string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Create-round form.
  const [theme, setTheme] = useState('');
  const [note, setNote] = useState('');
  const [media, setMedia] = useState<MediaType>('audio');
  const [pace, setPace] = useState<Pace>('steady');
  const [exhibition, setExhibition] = useState(false);

  const allowedPaces = league.data?.allowed_paces ?? (['rapid', 'steady', 'slow'] as Pace[]);
  useEffect(() => {
    if (league.data && !league.data.allowed_paces.includes(pace)) setPace(league.data.allowed_paces[0] ?? 'steady');
  }, [league.data]); // eslint-disable-line react-hooks/exhaustive-deps

  async function run(key: number | string, fn: () => Promise<unknown>, confirmMsg?: string) {
    if (busy != null) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key); setErr(null); setMsg(null);
    try { await fn(); round.reload(); list.reload(); proposals.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'That action failed — try again.'); }
    finally { setBusy(null); }
  }

  async function create() {
    if (!theme.trim() || busy != null) return;
    setBusy('create'); setErr(null); setMsg(null);
    try {
      const r = await adminCreateRound({ theme: theme.trim(), theme_note: note.trim() || undefined, media_type: media, is_exhibition: exhibition, pace });
      setTheme(''); setNote(''); setMedia('audio'); setExhibition(false);
      setMsg(r.opened ? `Round #${r.sequence_no} created and opened for submissions.` : `Round #${r.sequence_no} created and scheduled.`);
      round.reload(); list.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not create the round.');
    } finally { setBusy(null); }
  }

  // Owner gate — me is scoped to the active league, so is_admin = "owner here".
  if (me.loading) return <Shell><div className={s.state}>Loading…</div></Shell>;
  if (!me.data?.user?.is_admin) {
    return (
      <Shell>
        <div className={s.appbar}><span className={s.ctx}>Admin</span><button className={s.menu} aria-label="Back" onClick={() => nav?.go('home')}>⋯</button></div>
        <div className={s.state}>Only the league owner can manage rounds and settings.</div>
      </Shell>
    );
  }

  const rounds = [...(list.data ?? [])].sort((a, b) => a.sequence_no - b.sequence_no);
  const openId = round.data?.round_id ?? null;

  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>Admin console</span>
        <button className={s.menu} aria-label="Back" onClick={() => nav?.go('home')}>⋯</button>
      </div>

      {err ? <div className={s.banner} style={{ color: 'var(--video)' }}>{err}</div> : null}
      {msg ? <div className={s.banner} style={{ color: 'var(--good)' }}>{msg}</div> : null}

      <div className={s.sec}>Rounds</div>
      {list.loading ? (
        <div className={s.state}>Loading rounds…</div>
      ) : rounds.length === 0 ? (
        <div className={s.empty}>No rounds yet — create the first one below.</div>
      ) : (
        <div className={s.rounds}>
          {rounds.map((rr, i, arr) => {
            const open = rr.round_id === openId;
            const b = (k: string) => busy === `${k}:${rr.round_id}`;
            const upOff = !arr.slice(0, i).some((x) => x.phase === 'SCHEDULED');
            const downOff = !arr.slice(i + 1).some((x) => x.phase === 'SCHEDULED');
            return (
              <div key={rr.round_id} className={`${s.rrow}${open ? ' ' + s.ropen : ''}`}>
                <span className={s.rcov} style={{ background: gradFor(rr.round_id) }}><span className={s.rseq}>{String(rr.sequence_no).padStart(2, '0')}</span></span>
                <span className={s.rmeta}>
                  <b>{rr.theme}{rr.is_exhibition ? <em className={s.exh}>EXH</em> : null}</b>
                  <small>{rr.media_type} · {PHASE_LABEL[rr.phase].toLowerCase()}{open && round.data ? ` · ${round.data.phase === 'VOTING' ? `${round.data.ballots_in}/${round.data.expected_players} voted` : `${round.data.submissions_in}/${round.data.expected_players} in`}` : ''}</small>
                </span>
                <span className={s.ractions}>
                  {rr.phase === 'SCHEDULED' ? (
                    <>
                      <button className={s.mini} disabled={busy != null || upOff} title="Move earlier" onClick={() => run(`up:${rr.round_id}`, () => adminReorderRound(rr.round_id, 'up'))}>↑</button>
                      <button className={s.mini} disabled={busy != null || downOff} title="Move later" onClick={() => run(`down:${rr.round_id}`, () => adminReorderRound(rr.round_id, 'down'))}>↓</button>
                      <button className={s.act} disabled={busy != null} onClick={() => run(`open:${rr.round_id}`, () => adminOpenRound(rr.round_id))}>{b('open') ? '…' : 'Open'}</button>
                      <button className={`${s.act} ${s.danger}`} disabled={busy != null} onClick={() => run(`del:${rr.round_id}`, () => adminDeleteRound(rr.round_id), `Delete “${rr.theme}”? This can’t be undone.`)}>Delete</button>
                    </>
                  ) : rr.phase === 'SUBMITTING' ? (
                    <>
                      <button className={s.act} disabled={busy != null} onClick={() => run(`csub:${rr.round_id}`, () => adminCloseSubmissions(rr.round_id))}>{b('csub') ? '…' : 'Close subs'}</button>
                      <button className={`${s.act} ${s.ghost}`} disabled={busy != null} title="Back to scheduled" onClick={() => run(`un:${rr.round_id}`, () => adminUnopenRound(rr.round_id))}>Un-open</button>
                    </>
                  ) : rr.phase === 'VOTING' ? (
                    <button className={`${s.act} ${s.danger}`} disabled={busy != null} onClick={() => run(`close:${rr.round_id}`, () => adminCloseRound(rr.round_id), 'Close voting and score this round? Identities are revealed.')}>{b('close') ? '…' : 'Close & score'}</button>
                  ) : (
                    <button className={`${s.act} ${s.ghost}`} disabled={busy != null} onClick={() => run(`re:${rr.round_id}`, () => adminRescore(rr.round_id), 'Re-score this closed round?')}>{b('re') ? '…' : 'Re-score'}</button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {isJukebox ? (
        <>
          <div className={s.sec}>Member proposals</div>
          <div className={s.juke}>
            <span className={s.jl}>
              <b>{proposals.loading ? '…' : `${proposals.data?.length ?? 0} proposed`} · {proposalsOpen ? 'proposals open' : 'proposals closed'}</b>
              <small>Members propose rounds; the draw shuffles them into a random running order and schedules them.</small>
            </span>
            <div className={s.jact}>
              {proposalsOpen ? (
                <button className={`${s.act} ${s.ghost}`} disabled={busy != null} onClick={() => setProposals(false)}>{busy === 'propwin' ? '…' : 'Close'}</button>
              ) : (
                <button className={s.act} disabled={busy != null || seasonId == null} onClick={() => setProposals(true)}>{busy === 'propwin' ? '…' : 'Open proposals'}</button>
              )}
              <button className={`${s.act} ${s.ghost}`} disabled={busy != null} onClick={() => nav?.go('propose')}>View</button>
              <button className={s.act} disabled={busy != null || seasonId == null || (proposals.data?.length ?? 0) === 0} onClick={() => seasonId != null && run('draw', () => drawSeason(seasonId), 'Draw now? This shuffles every proposal into the running order and closes proposals.')}>{busy === 'draw' ? '…' : 'Draw the season'}</button>
            </div>
          </div>
          <div className={s.juke} style={{ marginTop: 8 }}>
            <span className={s.jl}><b>Per-member limit</b><small>How many rounds each member may propose.</small></span>
            <div className={s.jact}>
              <input className={s.input} style={{ width: 64 }} type="text" inputMode="numeric" value={capStr} onChange={(e) => setCapStr(e.target.value)} aria-label="Per-member round limit" />
              <button className={s.act} disabled={busy != null || seasonId == null || capStr.trim() === String(linfo.data?.proposals_per_member ?? '')} onClick={saveCap}>{busy === 'cap' ? '…' : 'Save'}</button>
            </div>
          </div>
        </>
      ) : null}

      {isJukebox ? (
        // Jukebox rounds come from member proposals → the draw, so no manual create form.
        // (Owners still open/close proposals and draw above; reopen proposals to gather more.)
        null
      ) : (
        <>
          <div className={s.sec}>Create a round</div>
          <div className={s.form}>
            <input className={s.input} placeholder="Theme — e.g. One-hit wonders" value={theme} onChange={(e) => setTheme(e.target.value)} maxLength={80} />
            <input className={s.input} placeholder="Note (optional) — e.g. One hit. Not two." value={note} onChange={(e) => setNote(e.target.value)} maxLength={140} />
            <div className={s.frow}>
              <select className={s.select} value={media} onChange={(e) => setMedia(e.target.value as MediaType)}>
                {MEDIA.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
              {allowedPaces.length > 1 ? (
                <select className={s.select} value={pace} onChange={(e) => setPace(e.target.value as Pace)}>
                  {PACES.filter((p) => allowedPaces.includes(p.key)).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              ) : null}
            </div>
            <label className={s.check}>
              <input type="checkbox" checked={exhibition} onChange={(e) => setExhibition(e.target.checked)} />
              <span>Exhibition round — excluded from the boards</span>
            </label>
            <button className={s.create} disabled={!theme.trim() || busy != null} onClick={create}>{busy === 'create' ? 'Creating…' : 'Create round'}</button>
          </div>
        </>
      )}

      <div className={s.sec}>Settings &amp; tools</div>
      <div className={s.mlist}>
        <MRow label="League settings" sub="Name, invite code, mode, pace, scoring, veto, media, pause, delete" onClick={() => nav?.go('settings')} />
        <MRow label="Scoring presets" sub="Named rulesets for member rounds" onClick={() => nav?.go('presets')} />
        <MRow label="People & Spotify" sub="Roster, claims, raw scoring numbers, playlists" onClick={() => navigate('/admin')} />
      </div>
    </Shell>
  );
}
